<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="visible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Two-Factor Authentication (2FA)</h1>
        <div class="modal-actions col-2">
          <el-button type="default" @click="cancelOperation"> Cancel</el-button>
        </div>
      </div>
    </div>
    <el-steps :active="activeStep" finish-status="success">
      <el-step></el-step>
      <el-step></el-step>
    </el-steps>

    <div id="step-1" v-if="activeStep == 0">
      <form @submit.prevent="sendCode">
        <h2 class="form-section-header">Setup SMS Authentication</h2>
        <p class="black-text">
          Get authentication codes by SMS on your mobile phone when signing into
          VOXO. Make sure that your country or region is supported for SMS
          delivery.
        </p>
        <div class="el-input el-input--prefix">
          <input
            v-model="phoneNumber"
            ref="number"
            autocomplete="off"
            type="text"
            placeholder="Enter number"
            class="el-input__inner"
            v-mask="'(###)-###-####'"
          />
          <span class="el-input__prefix">Phone Number</span>
        </div>

        <div class="flex text-center center pt-20 pb-10">
          <el-button
            native-type="submit"
            type="primary"
            :disabled="processing || !smsFormValid"
            v-loading="processing"
          >
            Send code
          </el-button>
        </div>
      </form>
    </div>

    <div id="step-2" v-if="activeStep == 1">
      <form @submit.prevent="verifyMFA">
        <h2 class="form-section-header">Verify the code sent to your phone</h2>

        <div class="el-input el-input--prefix">
          <el-input
            v-model="otp"
            ref="code"
            placeholder="XXXXXX"
            maxlength="6"
            v-mask="'######'"
            v-loading="processing"
          >
            <template slot="prefix">Code</template>
          </el-input>
        </div>
        <div class="text-center">
          <p class="black-text" style="font-size: 14px">
            Didn't get a code?
            <el-button
              class="ml-5"
              v-loading="resendProcessing"
              native-type="button"
              type="text"
              size="small"
              style="padding: 0"
              :disabled="resendProcessing"
              @click="resendOTP"
            >
              Resend code
            </el-button>
          </p>
        </div>
      </form>
    </div>
  </el-dialog>
</template>

<script>
import { apiV2Host } from "@/feathers/feathers-client";

export default {
  name: "MfaSMS",

  props: {
    visible: { required: true },
  },

  data() {
    return {
      activeStep: 0,
      processing: false,
      resendProcessing: false,
      phoneNumber: "",
      otp: "",
    };
  },

  watch: {
    otp() {
      if (this.otp.length === 6) {
        this.verifyMFA();
      }
    },

    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.number.focus();
        });
      }
    },

    activeStep() {
      if (this.activeStep === 1) {
        this.$nextTick(() => {
          this.$refs.code.focus();
        });
      }
    }
  },

  computed: {
    headers() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
        Accept: "application/json",
      };
    },

    smsFormValid() {
      return this.phoneNumber && this.phoneNumber.length === 14;
    },

    verifyFormValid() {
      return this.otp && this.otp.length === 6;
    },
  },

  methods: {
    cancelOperation() {
      this.phoneNumber = "";
      this.otp = "";
      this.activeStep = 0;
      this.$emit("cancelOperation");
    },

    notifyGenericError() {
      this.$notify({
        title: "Something Went Wrong!",
        message: "Please try again later!",
        type: "error",
      });
    },

    errorOnApiCall(json = {}, notify = true) {
      const { code, name, message } = json;
      if (code && ["4", "5"].includes(`${code}`[0])) {
        if (notify) {
          this.$notify({
            title: name,
            message: message,
            type: "error",
          });
        }
        return true;
      }

      return false;
    },

    async refreshUserInfo() {
      try {
        await this.$store.dispatch("auth/authenticate", {
          strategy: "jwt",
          accessToken: this.$store.state.auth.accessToken,
        });
      } catch (_e) {
        //
      }
    },

    async sendCode() {
      this.processing = true;

      const body = {
        mode: "sms",
        phoneNumber: this.phoneNumber.replace(/-|\(|\)/g, ""),
      };

      try {
        const res = await fetch(`${apiV2Host}/mfa/auth/setup`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: this.headers,
        });

        const json = await res.json();

        if (this.errorOnApiCall(json)) {
          this.processing = false;
          return;
        }

        this.phoneNumber = json.phoneNumber;

        await this.refreshUserInfo();

        this.processing = false;
        this.activeStep++;

        this.$notify({
          title: "Success",
          message: "A 6-digit code has been sent to your phone.",
          type: "success",
        });
      } catch (_e) {
        console.dir(_e);
        this.notifyGenericError();
        this.processing = false;
      }
    },

    async resendOTP() {
      try {
        this.resendProcessing = true;

        const body = {
          mode: "sms",
          phoneNumber: this.phoneNumber,
        };

        const res = await fetch(`${apiV2Host}/mfa/auth/resend-otp`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: this.headers,
        });

        const json = await res.json();

        if (this.errorOnApiCall(json)) {
          this.resendProcessing = false;
          return;
        }

        this.resendProcessing = false;

        this.$notify({
          title: "Check your inbox",
          message: "A 6-digit code has been resent to your phone.",
          type: "success",
        });
      } catch (_e) {
        this.notifyGenericError();
        this.resendProcessing = false;
      }
    },

    async verifyMFA() {
      const body = {
        otp: this.otp,
        mode: "sms",
        phoneNumber: this.phoneNumber,
      };
      this.processing = true;

      try {
        const res = await fetch(`${apiV2Host}/mfa/auth/verify`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: this.headers,
        });

        const json = await res.json();

        if (this.errorOnApiCall(json)) {
          this.processing = false;
          this.otp = "";
          return;
        }

        await this.refreshUserInfo();

        this.processing = false;
        this.activeStep = 0;

        this.otp = "";
        this.phoneNumber = "";

        this.$notify({
          title: "Success",
          message: "Successfully configured SMS Authentication!",
          type: "success",
        });

        this.$emit("verified");
      } catch (_e) {
        this.notifyGenericError();
        this.processing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.el-steps {
  width: 150px;
  margin: 40px auto 20px auto;

  @media only screen and (max-width: 1320px) {
    margin-top: 60px;
  }
}

form {
  max-width: 600px;
  margin: 0 auto;
}
</style>
